import type { ReplicaResponse, ReplicaResponsePart } from '@unni-craftsman/turso-fts-common/dist/replication'
import { sendBudgetedPush } from '../../../db-replication/src/client/push-budget'
import type { FailureScope, OwnerCardDerivation, ReceivedContent } from '@unni-craftsman/turso-fts-common'
export class ReplicaTransportError extends Error {
  constructor(public readonly status: number, public readonly code?: string, message = `Replication transport rejected request (${status})`, cause?: unknown, public readonly deleted = false) {
    super(message, cause === null ? undefined : { cause })
    this.name = 'ReplicaTransportError'
  }
}

export type ReplicaTransport = ReturnType<typeof createReplicaTransport>

export type ReplicaTransportConfig = {
  requestBytes?: number
  identity: Uint8Array
  localTables?: string[]
  observedTables?: string[]
  failureScope?: FailureScope
  ownerCardDerivations?: OwnerCardDerivation[]
  receivedContent?: ReceivedContent[]
  /** Columns that place a table's rows in their access scope, declared by the app; the engine keeps them in the key image of
   * a published update or delete beside the primary key, so the server can authorize it (fork ReplicaConfig::scope_columns). */
  scopeColumns?: { table: string; column: string }[]
  sendUrl: string
  fetchUrl: string
  admitUrl?: string
  headers?: Record<string, string>
}

export function createReplicaTransport(config: ReplicaTransportConfig, fetch: (url: RequestInfo | URL, init?: RequestInit) => Promise<Response> = globalThis.fetch, tel: Tel = nullTel) {
  const { sendUrl, fetchUrl } = config
  const headers: Record<string,string> = { ...config.headers, 'content-type': 'application/octet-stream' }
  let identity = new Uint8Array(config.identity)
  return {
    sendRemote(bytes: Uint8Array, signal?: AbortSignal, context: Tel = tel) {
      return sendBudgetedPush(bytes, async body => { const result = await request('send', sendUrl, body, signal, context); if (!(result instanceof Uint8Array)) throw new Error('Send requires a binary receipt'); return result }, config.requestBytes)
    },
    fetchRemote: (bytes: Uint8Array, signal?: AbortSignal, context: Tel = tel) => request('fetch', fetchUrl, bytes, signal, context),
  }

  async function request(operation: 'send' | 'fetch', url: string, bytes: Uint8Array, signal: AbortSignal | undefined, context: Tel): Promise<ReplicaResponse> {
    const requested = requestIdentity(bytes)
    if (config.admitUrl && requested && !requested.every((byte,index) => byte === identity[index])) {
      const replica = Array.from(requested, byte => byte.toString(16).padStart(2,'0')).join('')
      const admission = await fetch(config.admitUrl, { method: 'POST', credentials: 'include', signal,
        headers: { 'content-type': 'application/json' }, body: JSON.stringify({ replica }) })
      if (!admission.ok) {
        throw responseFailure(admission.status, await admission.json())
      }
      const body = await admission.json() as { generation: number }
      if (!Number.isSafeInteger(body.generation) || body.generation < 1) throw new Error('Invalid replica admission')
      headers['x-replica-generation'] = String(body.generation)
      identity = new Uint8Array(requested)
    }
    let response: Response
    const scopedFetch = context === nullTel ? fetch : createRelayFetch({ tel: context, mode: 'native', telemetryUrl: () => ({ path: `/api/replica/${operation}` }), fetch })
    try { response = await scopedFetch(url, { method: 'POST', credentials: 'include', headers: context === nullTel ? { ...headers, ...NOT_SAMPLED_HEADERS.native } : headers, body: new Uint8Array(bytes), signal }) }
    catch (error) {
      if (signal?.aborted || error instanceof Error && error.name === 'AbortError') throw error
      throw new ReplicaTransportError(0, 'offline', 'Replica transport unavailable', error)
    }
    if (!response.ok) {
      const body: unknown = response.headers.get('content-type')?.includes('application/json') ? await response.json() : undefined
      throw responseFailure(response.status, body)
    }
    if (response.headers.get('content-type')?.includes('text/event-stream')) return responseParts(response, getRelayFetchTel(response) ?? context)
    return new Uint8Array(await response.arrayBuffer())
  }
}

function responseFailure(status: number, body: unknown): ReplicaTransportError {
  const failure = body && typeof body === 'object' ? body as Record<string, unknown> : undefined
  const message = typeof failure?.message === 'string' ? failure.message : typeof failure?.error === 'string' ? failure.error : undefined
  return new ReplicaTransportError(status, typeof failure?.code === 'string' ? failure.code : undefined, message, undefined, failure?.deleted === true)
}

export function replicaTransportKey(config?: ReplicaTransportConfig): string {
  return config ? JSON.stringify([Array.from(config.identity), config.sendUrl, config.fetchUrl, config.admitUrl, config.requestBytes, config.localTables, config.observedTables, config.ownerCardDerivations, config.receivedContent, config.scopeColumns, config.failureScope && [config.failureScope.workspace, config.failureScope.principal, String(config.failureScope.generation), Array.from(config.failureScope.replica)], Object.entries(config.headers ?? {}).toSorted(([a], [b]) => a.localeCompare(b))]) : 'local'
}

export function copyReplicaTransport(config?: ReplicaTransportConfig): ReplicaTransportConfig | undefined {
  if (!config) return void 0
  if (config.identity.length !== 16) throw new Error('Replica identity must contain 16 bytes')
  const scope = config.failureScope
  if (scope && (scope.replica.length !== 16 || !scope.replica.every((byte, index) => byte === config.identity[index]))) throw new Error('Failure scope must match replica identity')
  return { identity: new Uint8Array(config.identity), requestBytes: config.requestBytes, localTables: config.localTables && [...config.localTables], observedTables: config.observedTables && [...config.observedTables], receivedContent: config.receivedContent?.map(binding => ({ ...binding, catalogFingerprint: new Uint8Array(binding.catalogFingerprint) })), ownerCardDerivations: config.ownerCardDerivations?.map(binding => ({ ...binding, triggerFingerprint: new Uint8Array(binding.triggerFingerprint), catalogFingerprint: new Uint8Array(binding.catalogFingerprint) })), scopeColumns: config.scopeColumns?.map(scope => ({ ...scope })), failureScope: scope && { ...scope, replica: new Uint8Array(scope.replica) }, sendUrl: config.sendUrl, fetchUrl: config.fetchUrl, admitUrl: config.admitUrl, headers: config.headers && { ...config.headers } }
}
import { createRelayFetch, getRelayFetchTel, NOT_SAMPLED_HEADERS, nullTel, type Tel, type BoundaryTelemetry } from '@w/tel'

/** Native exchange metadata only; resets put their fresh identity in field 2. */
function requestIdentity(bytes: Uint8Array): Uint8Array | null {
  let offset = 0
  function integer() {
    let factor = 1, value = 0
    for (;;) {
      const byte = bytes[offset++]
      if (byte === undefined) return null
      value += (byte & 127) * factor
      if (byte < 128) return value
      factor *= 128
      if (!Number.isSafeInteger(factor)) return null
    }
  }
  while (offset < bytes.length) {
    const tag = integer()
    if (tag === null) return null
    if ((tag & 7) === 0) { if (integer() === null) return null; continue }
    if ((tag & 7) !== 2) return null
    const length = integer()
    if (length === null || offset + length > bytes.length) return null
    if (tag >>> 3 === 2) return length === 16 ? bytes.slice(offset,offset+length) : null
    offset += length
  }
  return null
}

/** SSE line framing follows EventSource: data lines join, blank lines dispatch; decoder spans byte chunks. */
async function* responseParts(response: Response, tel: Tel): AsyncGenerator<ReplicaResponsePart> {
  const reader = response.body?.getReader()
  if (!reader) throw new Error('Missing replica stream')
  const decoder = new TextDecoder()
  let buffer = '', data = '', event = ''
  let complete: ReplicaResponsePart | undefined
  let failure: ReplicaTransportError | undefined
  try {
    for (;;) {
      const chunk = await reader.read()
      buffer += decoder.decode(chunk.value, { stream: !chunk.done })
      let end: number
      while ((end = buffer.search(/[\r\n]/)) >= 0) {
        if (buffer[end] === '\r' && end === buffer.length - 1 && !chunk.done) break
        const line = buffer.slice(0, end)
        buffer = buffer.slice(end + (buffer[end] === '\r' && buffer[end + 1] === '\n' ? 2 : 1))
        if (line === '') {
          if (data) {
            const body = JSON.parse(data) as { bytes: string; revision: string; cursor: string }
            if (event === 'begin') yield { type: 'Begin', bytes: Uint8Array.fromBase64(body.bytes) }
            else if (event === 'transaction') yield { type: 'Transaction', revision: BigInt(body.revision), bytes: Uint8Array.fromBase64(body.bytes) }
            else if (event === 'page-end') yield { type: 'PageEnd', cursor: BigInt(body.cursor) }
            else if (event === 'complete') complete = { type: 'Complete', cursor: BigInt(body.cursor) }
            else if (event === 'error') failure = responseFailure((body as unknown as { status: number }).status, body)
            else if (event === 'telemetry') {
              const telemetry = body as unknown as BoundaryTelemetry | null
              if (telemetry) tel._insertBoundaryTrace(telemetry.trace, telemetry.metrics, telemetry.resource)
            }
          }
          data = ''; event = ''
        } else if (!line.startsWith(':')) {
          const colon = line.indexOf(':')
          const field = colon < 0 ? line : line.slice(0, colon)
          const value = colon < 0 ? '' : line.slice(colon + 1).replace(/^ /, '')
          if (field === 'event') event = value
          if (field === 'data') data += (data ? '\n' : '') + value
        }
      }
      if (chunk.done) break
    }
    if (failure) throw failure
    if (!complete) throw new Error('Truncated replica stream')
    yield complete
  } finally { await reader.cancel(); reader.releaseLock() }
}
